import { Link } from 'react-router-dom'
import { useReadFiscalNoteList } from '@/api/accounting-dealer'
import { FiscalNoteStatus, RouterLink } from '@/components'
import { useCompany } from '@/context/companies'
import { formatCurrency } from '@/helpers'
import { routes } from '@/router'
import { ErrorOutline, ReceiptOutlined } from '@mui/icons-material'
import {
  Card,
  Box,
  Typography,
  Theme,
  useMediaQuery,
  Divider,
  Button,
  Stack,
  Skeleton,
} from '@mui/material'
import { formatISO, subDays } from 'date-fns'

const CURRENT_DATE = new Date()

export function FiscalNotes() {
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'))

  const { company } = useCompany()

  const { data: fiscalNoteList, isLoading } = useReadFiscalNoteList(
    {
      company_key: company.key,
      start_date: formatISO(subDays(CURRENT_DATE, 30), {
        representation: 'date',
      }),
      end_date: formatISO(CURRENT_DATE, { representation: 'date' }),
    },
    {
      enabled: !isMobile,
    },
  )

  if (isMobile) {
    return (
      <RouterLink to={routes.fiscalNotes.index}>
        <Card>
          <Box textAlign="right">
            <ReceiptOutlined sx={{ color: 'secondary.main', fontSize: 32 }} />
          </Box>
          <Typography fontSize={18}>
            Notas
            <br />
            Fiscais
          </Typography>
        </Card>
      </RouterLink>
    )
  }

  if (isLoading) {
    return (
      <Card>
        <Box display="flex" justifyContent="space-between">
          <Skeleton width={140} sx={{ fontSize: 26 }} />
          <Skeleton width={100} sx={{ fontSize: 26 }} />
        </Box>

        <Stack>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} />
          ))}
        </Stack>
      </Card>
    )
  }

  if (!fiscalNoteList) {
    return (
      <Card sx={{ textAlign: 'center' }}>
        <ErrorOutline color="error" fontSize="large" />
        <Typography color="gray">
          Não foi possível carregar as notas fiscais.
        </Typography>
      </Card>
    )
  }

  const lastFiveFiscalNotes = fiscalNoteList.data.slice(0, 5)

  return (
    <Card>
      <Typography
        fontSize={20}
        mb={2}
        display="flex"
        justifyContent="space-between"
      >
        Notas Fiscais
        <Button
          component={Link}
          to={routes.fiscalNotes.index}
          size="small"
          variant="text"
          sx={{ color: 'primary.light' }}
        >
          Ver todas
        </Button>
      </Typography>

      {lastFiveFiscalNotes.length > 0 ? (
        <Stack>
          {lastFiveFiscalNotes.map(({ id, status, borrower, value }) => (
            <Box
              key={id}
              sx={{
                '&:last-child .MuiDivider-root:last-of-type': {
                  display: 'none',
                },
              }}
            >
              <Box display="flex" alignItems="center" gap={3} py={0.5}>
                <FiscalNoteStatus status={status} type="dot" />
                <Typography flex={1}>{borrower.name}</Typography>
                <Typography>{formatCurrency(value)}</Typography>
              </Box>

              <Divider />
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography color="textDisabled" py={3} textAlign="center">
          Nenhuma nota fiscal recente
        </Typography>
      )}
    </Card>
  )
}
